import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { AdapterService } from '../services/adapter.service';
import { SquareCatalogItemService } from '../services/square-catalog-item.service';
import * as SquareCatalogItemActions from './square-catalog-item.actions';

@Injectable()
export class SquareCatalogItemEffects {
    private _actions$ = inject(Actions);
    private _squareCatalogItemService = inject(SquareCatalogItemService);
    private _alertService = inject(AlertService);
    private _adapter = inject(AdapterService);

    messages = {
        getAllError: 'Error getting all square catalog items',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllSquareCatalogItems$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCatalogItemActions.loadSquareCatalogItems),
            switchMap((action) =>
                this._squareCatalogItemService.getAll(action.params).pipe(
                    switchMap((res) => {
                        const adapted = this._adapter.adaptAll(res.data);
                        const catalogMismatch = this._adapter.adaptMismatch(
                            res.mismatch
                        );

                        return [
                            SquareCatalogItemActions.loadSquareCatalogItemsSuccess(
                                { squareCatalogItems: adapted }
                            ),
                            SquareCatalogItemActions.setCatalogMismatch({
                                catalogMismatch,
                            }),
                        ];
                    }),
                    catchError((error) =>
                        of(
                            SquareCatalogItemActions.loadSquareCatalogItemsFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Get One
    getSquareCatalogItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCatalogItemActions.loadSquareCatalogItem),
            switchMap((action) =>
                this._squareCatalogItemService.getOne(action.id).pipe(
                    map((res) =>
                        SquareCatalogItemActions.loadSquareCatalogItemSuccess({
                            squareCatalogItem: res.data,
                        })
                    ),
                    catchError((error) =>
                        of(
                            SquareCatalogItemActions.loadSquareCatalogItemFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Create
    createSquareCatalogItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCatalogItemActions.createSquareCatalogItem),
            switchMap((action) =>
                this._squareCatalogItemService.create(action.data).pipe(
                    map((res) => {
                        const adapted = this._adapter.adaptSingle(res.data);
                        return SquareCatalogItemActions.createSquareCatalogItemSuccess(
                            {
                                squareCatalogItem: adapted,
                                success: 'Catalog item created successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            SquareCatalogItemActions.createSquareCatalogItemFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                )
            )
        )
    );

    // Update
    updateSquareCatalogItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCatalogItemActions.updateSquareCatalogItem),
            switchMap((action) =>
                this._squareCatalogItemService
                    .update(action.data, action.squareId)
                    .pipe(
                        map((res) => {
                            const adapted = this._adapter.adaptSingle(res.data);
                            return SquareCatalogItemActions.updateSquareCatalogItemSuccess(
                                {
                                    squareCatalogItem: adapted,
                                    success:
                                        'Catalog item updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                SquareCatalogItemActions.updateSquareCatalogItemFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // Delete
    deleteSquareCatalogItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(SquareCatalogItemActions.deleteSquareCatalogItem),
            switchMap((action) =>
                this._squareCatalogItemService
                    .delete(action.squareId, action.params)
                    .pipe(
                        map(() =>
                            SquareCatalogItemActions.deleteSquareCatalogItemSuccess(
                                {
                                    squareId: action.squareId,
                                    success:
                                        'Catalog item deleted successfully',
                                }
                            )
                        ),
                        catchError((error) =>
                            of(
                                SquareCatalogItemActions.deleteSquareCatalogItemFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Side Effects (alerts)
    // -----------------------------------------------------------

    onSquareCatalogItemActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareCatalogItemActions.createSquareCatalogItemSuccess,
                    SquareCatalogItemActions.updateSquareCatalogItemSuccess,
                    SquareCatalogItemActions.deleteSquareCatalogItemSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onSquareCatalogItemActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    SquareCatalogItemActions.loadSquareCatalogItemsFailure,
                    SquareCatalogItemActions.loadSquareCatalogItemFailure,
                    SquareCatalogItemActions.createSquareCatalogItemFailure,
                    SquareCatalogItemActions.updateSquareCatalogItemFailure,
                    SquareCatalogItemActions.deleteSquareCatalogItemFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
